<template>
    <div>
        <b-form ref="document-share-form" @submit.prevent="saveForm" @keydown.enter="saveForm">
            <b-form-group id="input-group-1" label-class="gk-label-margin gk-modal-label" label-for="input-1">
                <template slot="label">
                    <span v-translate>Subject*</span>
                </template>
                <div v-if="!currentCompany.is_premium" class="premium-info text-unassigned" id="non-premium-text" v-b-tooltip.hover.top="$gettext('You can edit the subject by upgrading to Premium')"> 
                    <b-input-group>
                        <b-form-input id="input-1" v-model="sendDetails.subject" required :disabled="!currentCompany.is_premium"></b-form-input>
                    </b-input-group>
                </div>
                <div v-if="currentCompany.is_premium"> 
                    <b-input-group>
                        <b-form-input id="input-1" v-model="sendDetails.subject" required :disabled="!currentCompany.is_premium"></b-form-input>
                    </b-input-group>
                </div>
            </b-form-group>

            <b-form-group id="input-group-2" label-class="gk-label-margin gk-modal-label" label-for="input-2">
                <template slot="label">
                    <span v-translate>Recipients*</span>
                </template>
                <b-input-group>
                    <EmailFormInput ref="email_form_input" />
                </b-input-group>
            </b-form-group>
        
            <b-form-group id="input-group-3" label-class="gk-label-margin gk-modal-label" label-for="input-3">
                <template slot="label">
                    <span v-translate>Message*</span>
                </template>
                <b-input-group>
                    <div  class="premium-textarea-wrapper">
                        <b-form-textarea id="input-3" v-model="sendDetails.message" required :placeholder="$gettext('Dear...')" rows="6" no-resize />
                        <div v-if="!currentCompany.is_premium" class="premium-info text-unassigned" id="non-premium-text" v-b-tooltip.hover.top="$gettext('You can remove this text by upgrading to Gekko Premium')">
                            <p>
                                <span>{{ premiumText }}</span>
                            </p>
                            <p v-translate>Gekko: the friendly and free online bookkeeping tool for small business owners, <span>https://getgekko.com</span></p>
                        </div>
                    </div>
                </b-input-group>
            </b-form-group>

            <b-form-group id="input-group-4" label-class="gk-label-margin gk-modal-label" label-for="input-4">
                <template slot="label">
                    <span v-translate>Attachments</span>
                </template>
                <div class="document-table-wrap mb-2">
                    <b-table 
                        :items="selected"
                        :fields="fields"
                        class="document-table"
                        :class="['gk-table gk-table--hover gk-table--responsive gk-table--headed gk-table--medium']"
                        responsive
                    >
                        <template #cell(file_size)="row">
                            {{ niceBytes(row.item.file_size, 1000, true) }}
                        </template>
                        <template #cell(actions)="data">
                            <GekkoButton 
                                @click="removeSelectedDocument(data)"
                                icon="fa-solid fa-trash"
                                variant="link"
                                class="text-danger"/>
                        </template>
                    </b-table>
                </div>

                <div>
                    <div v-show="showDocumentSelector">
                        <DocumentSelector 
                            ref="document_selector"
                            :exclude-files="($route.params['documentId']) ? [parseInt($route.params['documentId'])]: []"
                            @documentSelected="onDocumentSelected"
                        />
                    </div>

                    <GekkoButton 
                        v-if="!showDocumentSelector"
                        @click="addFile()"
                        :text="$gettext('Add another file')"
                        icon="fa-solid fa-plus"
                        variant="link"
                        class="px-0"
                    />
                </div>
            </b-form-group>
        </b-form>
    </div>
</template>

<script>
import { niceBytes } from "@/utils/";
import GekkoButton from "@/components/GekkoButton";
import DocumentService from "@/store/services/document.service";
import DocumentSelector from "@/components/documents/DocumentSelector";
import EmailFormInput from "@/components/ui/EmailFormInput";
import { mapGetters } from "vuex";

export default {
    name: "AddEditDocument",
    components: {GekkoButton, DocumentSelector, EmailFormInput},
    data() {
        return {
            sendDetails: {
                emails: "",
                message: "",
                subject: ""
            },
            showDocumentSelector: false,
            selected: [],
            fields: [
                {key: "title", label: this.$gettext('Title')},
                {key: "file_size", label: this.$gettext('File Size')},
                {key: "description", label: this.$gettext('Description')},
                {key: "actions", label: "", tdClass: "text-right"}
            ]
        }
    },
    computed: {
        ...mapGetters({ 
            currentCompany: "main/getCurrentCompany"
        }),
        documentsSelected () {
            if (this.selected.length > 1) {
                return this.$gettext('multiple documents')
            } else {
                if (this.selected.length == 1) {
                    return this.selected[0].title
                } else {
                    return this.$gettext('no document selected')
                }
            }
        },
        premiumText () {
          let text = this.$gettext('This email has been sent to you via Gekko on behalf of %{ company_name }.')
          text = text.replace("%{ company_name }", this.currentCompany.name);
          return text
        },
        template () {
            return this.documentsSelected + ' from ' + this.currentCompany.name;
        }
    },
    watch: {
        template: function() {
            if(this.sendDetails.subject.includes(" from ")){
                this.sendDetails.subject = this.template;
            }
        }
    },
    created() {
        if (this.$route.params['documentId']) {
            DocumentService.retrieve(this.$route.params['documentId']).then((response) => {
                this.selected.push(response.data)
                this.$refs.document_selector.removeItem(response.data)
            });
        }
        this.sendDetails.subject = this.template;
    },
    methods: {
        saveForm(ev) {
            let vm = this;
            if (ev && ev.key && ev.target.nodeName !== 'TEXTAREA') {
                ev.preventDefault()
                return
            }
            if (!ev || (ev && !ev.key)) { // avoid submitting on key events
                this.sendDetails.emails = this.$refs.email_form_input.getFullString();
                this.sendDetails.documents = this.selected.map((document) => document.id);

                if (this.sendDetails.emails === '' || this.sendDetails.documents.length === 0) {
                    const empty_fields = []
                    if (this.sendDetails.emails === '') {
                        empty_fields.push(this.$gettext('Recipients'))
                    }
                    if (this.sendDetails.documents.length === 0) {
                        empty_fields.push(this.$gettext('Attachments'))
                    }
                    this.showMissingFieldsAlert(empty_fields)
                    return
                }
                DocumentService.send(this.sendDetails).then((response) => {
                    if (response.status === 202) {
                        this.$router.push({name: "Documents"});
                        this.$store.commit('setAlert', {
                            title: this.$gettext("Documents sent"),
                            text: this.$gettext("The documents were sent successfully."),
                            variant:"success",
                            duration: 4
                        })
                    }
                }).catch((error) => {
                    if (error && error.status === 429) {
                        this.$store.commit('setAlert', {
                            title: this.$gettext("Documents not sent"),
                            text: this.$gettext("You need to wait before sending more documents."),
                            variant:"warning",
                            duration: 4
                        });
                        if (!this.currentCompany.is_premium) {
                            this.$store.commit('togglePremiumModal', 'web_email_throttle')
                        }
                    } else if (error && error.status === 400 && error.data) {
                        const empty_fields = []
                        if (Object.hasOwn(error.data, 'emails')) {
                            empty_fields.push(this.$gettext('Recipients'))
                        }
                        if (Object.hasOwn(error.data, 'message')) {
                            empty_fields.push(this.$gettext('Message'))
                        }
                        if (Object.hasOwn(error.data, 'documents')) {
                            empty_fields.push(this.$gettext('Attachments'))
                        }
                        this.showMissingFieldsAlert(empty_fields)
                    } else {
                        this.$store.commit('setAlert', {
                            title: this.$gettext("Error"),
                            text: this.$gettext("Error sending the documents."),
                            variant:"danger",
                            duration: 4
                        });
                    }
                });
            }
        },
        showMissingFieldsAlert (empty_fields) {
            let alert_msg = this.$gettext("The following fields are empty:")
            if (empty_fields.length > 0) {
                alert_msg = alert_msg + ' ' + empty_fields.join(', ')
            }
            this.$store.commit('setAlert', {
                title: this.$gettext("Missing fields"),
                text: alert_msg,
                variant:"danger",
                duration: 4
            })
        },
        addFile() {
            this.showDocumentSelector = !this.showDocumentSelector;
        },
        onDocumentSelected(document) {
            this.showDocumentSelector = !this.showDocumentSelector;
            this.selected.push(document);
        },
        removeSelectedDocument(data) {
            this.selected = this.selected.filter((document) => document.id != data.item.id);
            this.$refs.document_selector.documentList.splice(0, 0, data.item);
        },
        /**
         * Alias for utils/niceBytes
         */
        niceBytes (size, base, unit = false) {
          return niceBytes(size, base, unit)
        },
    }
}
</script>

<style lang="scss">
.documents-modal {
    .document-table-wrap {
        border: 1px solid $colorBorderDash;
        border-radius: $radiusLg;
        overflow: hidden;
        overflow-x: auto;
        .document-table {
            background: #f4f6f5;
            thead {
                background: #fff;
                text-transform: none;
                color: $colorTextBase;
            }
            td:nth-child(1){ width: 40%; }
            td:nth-child(2){ width: 15%; }
            td:nth-child(3){ width: 40%; }
            td:nth-child(4){ width: 5%; }
        }
    }
}
</style>